import {
  Text,
  SimpleGrid,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import FormInput from 'components/fields/home/FormInput';
import RadioInput from 'components/fields/home/RadioInput';
import SelectInput from 'components/fields/home/SelectInput';

import getRadioYesNo from 'helpers/getRadioYesNo';

import { changeDwellingRatingInformation } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface HeatingSystemProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const HeatingSystem = ({ dwelling, index }: HeatingSystemProps) => {
  const dispatch = useAppDispatch();
  const isBaraEnabled = useFeatureFlag('enable-bara');

  const ratingInfo = dwelling.rating_information;

  const onInputChange = (
    event: any,
    systemType: 'primary' | 'auxiliary',
    field: string,
  ) => {
    const { value } = event.target;
    const fieldName =
      systemType === 'primary'
        ? 'primary_heating_type'
        : 'auxiliary_heating_type';

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: fieldName,
        value: {
          ...dwelling.rating_information[fieldName],
          [field]: value,
        },
      }),
    );
  };

  const onRadioChange = (
    value: string,
    systemType: 'primary' | 'auxiliary',
    field: string,
  ) => {
    const fieldName =
      systemType === 'primary'
        ? 'primary_heating_type'
        : 'auxiliary_heating_type';

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: fieldName,
        value: {
          ...dwelling.rating_information[fieldName],
          [field]: value === 'true',
        },
      }),
    );
  };

  if (!ratingInfo) return null;

  return (
    <Box mb="20px">
      {/* Primary Heating System */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Primary Heating System
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Apparatus"
          name="primary_heating_apparatus"
          value={ratingInfo.primary_heating_type.apparatus || ''}
          onChange={(e: any) => onInputChange(e, 'primary', 'apparatus')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Fuel"
          name="primary_heating_fuel"
          value={ratingInfo.primary_heating_type.fuel || ''}
          onChange={(e: any) => onInputChange(e, 'primary', 'fuel')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Location"
          name="primary_heating_location"
          value={ratingInfo.primary_heating_type.location || ''}
          onChange={(e: any) => onInputChange(e, 'primary', 'location')}
        />
        <Box>
          <RadioInput
            section={SECTIONS.DWELLINGS}
            label="Professionally Installed"
            subLabel="Primary"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={getRadioYesNo(
              ratingInfo.primary_heating_type.is_professionally_installed,
            )}
            onChange={(value: string) =>
              onRadioChange(value, 'primary', 'is_professionally_installed')
            }
            stacked
            disabled
          />
          {ratingInfo.primary_heating_type.is_professionally_installed ===
            false && (
            <Alert status="error" mt="10px" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <AlertDescription>
                The heating system is not professionally installed. Please refer
                to BIG Underwriting
                {isBaraEnabled
                  ? ' or submit an After-Hours Referral (MP/Delegate)'
                  : ''}
                .
              </AlertDescription>
            </Alert>
          )}
        </Box>
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Approved ULC/CSA/WH"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.primary_heating_type.is_approved_ulc_csa_wh,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'primary', 'is_approved_ulc_csa_wh')
          }
          stacked
          disabled
        />
      </SimpleGrid>

      {/* Auxiliary Heating System */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Auxiliary Heating System
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Apparatus"
          name="auxiliary_heating_apparatus"
          value={ratingInfo.auxiliary_heating_type.apparatus || ''}
          onChange={(e: any) => onInputChange(e, 'auxiliary', 'apparatus')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Fuel"
          name="auxiliary_heating_fuel"
          value={ratingInfo.auxiliary_heating_type.fuel || ''}
          onChange={(e: any) => onInputChange(e, 'auxiliary', 'fuel')}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Location"
          name="auxiliary_heating_location"
          value={ratingInfo.auxiliary_heating_type.location || ''}
          onChange={(e: any) => onInputChange(e, 'auxiliary', 'location')}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Make"
          name="auxiliary_heating_make"
          value={ratingInfo.auxiliary_heating_type.make || ''}
          onChange={(e: any) => onInputChange(e, 'auxiliary', 'make')}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Year"
          name="auxiliary_heating_year"
          value={ratingInfo.auxiliary_heating_type.year || ''}
          onChange={(e: any) => onInputChange(e, 'auxiliary', 'year')}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Face Cords/Year"
          name="auxiliary_heating_face_cords"
          value={
            ratingInfo.auxiliary_heating_type.number_face_cords_per_year || ''
          }
          onChange={(e: any) =>
            onInputChange(e, 'auxiliary', 'number_face_cords_per_year')
          }
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Oil Tank Year"
          name="auxiliary_heating_oil_tank_year"
          value={ratingInfo.auxiliary_heating_type.oil_tank_year || ''}
          onChange={(e: any) => onInputChange(e, 'auxiliary', 'oil_tank_year')}
        />
        <SelectInput
          section={SECTIONS.DWELLINGS}
          label="Oil Tank Location"
          name="auxiliary_heating_oil_tank_location"
          value={ratingInfo.auxiliary_heating_type.oil_tank_location || ''}
          options={[
            { value: '', label: 'Select...' },
            { value: 'INSIDE', label: 'INSIDE' },
            { value: 'OUTSIDE', label: 'OUTSIDE' },
            { value: 'IN GROUND', label: 'IN GROUND' },
            { value: 'ABOVE GROUND', label: 'ABOVE GROUND' },
          ]}
          onChange={(e: any) =>
            onInputChange(e, 'auxiliary', 'oil_tank_location')
          }
        />
      </SimpleGrid>

      {/* Radiant Heating Area */}
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Radiant Heating Area (Sq Ft)"
          name="auxiliary_heating_radiant_area_sq_ft"
          value={
            ratingInfo.auxiliary_heating_type.radiant_heating_area
              .square_feet || ''
          }
          onChange={(event: any) => {
            const { value } = event.target;
            dispatch(
              changeDwellingRatingInformation({
                index,
                name: 'auxiliary_heating_type',
                value: {
                  ...ratingInfo.auxiliary_heating_type,
                  radiant_heating_area: {
                    ...ratingInfo.auxiliary_heating_type.radiant_heating_area,
                    square_feet: value,
                  },
                },
              }),
            );
          }}
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Radiant Heating Area (Sq M)"
          name="auxiliary_heating_radiant_area_sq_m"
          value={
            ratingInfo.auxiliary_heating_type.radiant_heating_area
              .square_meters || ''
          }
          onChange={(event: any) => {
            const { value } = event.target;
            dispatch(
              changeDwellingRatingInformation({
                index,
                name: 'auxiliary_heating_type',
                value: {
                  ...ratingInfo.auxiliary_heating_type,
                  radiant_heating_area: {
                    ...ratingInfo.auxiliary_heating_type.radiant_heating_area,
                    square_meters: value,
                  },
                },
              }),
            );
          }}
        />
      </SimpleGrid>

      {/* Additional Auxiliary Heating Fields */}
      <SimpleGrid columns={5} spacing="15px" mb="15px">
        <Box>
          <RadioInput
            section={SECTIONS.DWELLINGS}
            label="Professionally Installed"
            subLabel="Auxiliary"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={getRadioYesNo(
              ratingInfo.auxiliary_heating_type.is_professionally_installed,
            )}
            onChange={(value: string) =>
              onRadioChange(value, 'auxiliary', 'is_professionally_installed')
            }
            stacked
            disabled
          />
          {ratingInfo.auxiliary_heating_type.is_professionally_installed ===
            false && (
            <Alert status="error" mt="10px" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <AlertDescription>
                The heating system is not professionally installed. Please refer
                to BIG Underwriting
                {isBaraEnabled
                  ? ' or submit an After-Hours Referral (MP/Delegate)'
                  : ''}
                .
              </AlertDescription>
            </Alert>
          )}
        </Box>
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Approved ULC/CSA/WH"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.auxiliary_heating_type.is_approved_ulc_csa_wh,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'auxiliary', 'is_approved_ulc_csa_wh')
          }
          stacked
          disabled
        />
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Solid Fuel Questionnaire Attached"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.auxiliary_heating_type
              .is_solid_fuel_heating_questionnaire_attached,
          )}
          onChange={(value: string) =>
            onRadioChange(
              value,
              'auxiliary',
              'is_solid_fuel_heating_questionnaire_attached',
            )
          }
          stacked
        />
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Fuel Oil Tank Questionnaire Attached"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.auxiliary_heating_type
              .is_fuel_oil_tank_questionnaire_attached,
          )}
          onChange={(value: string) =>
            onRadioChange(
              value,
              'auxiliary',
              'is_fuel_oil_tank_questionnaire_attached',
            )
          }
          stacked
        />
      </SimpleGrid>

      {/* Heating Upgrades */}
      <Text
        fontWeight="medium"
        fontSize="md"
        mt="15px"
        mb="10px"
        color="gray.700"
      >
        Heating Upgrades
      </Text>
      <SimpleGrid columns={6} spacing="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Full Year Upgrade"
          name="upgrades_heating_full_year"
          value={ratingInfo.upgrades.heating.full_year || ''}
          onChange={(event: any) => {
            const { value } = event.target;
            dispatch(
              changeDwellingRatingInformation({
                index,
                name: 'upgrades',
                value: {
                  ...ratingInfo.upgrades,
                  heating: {
                    ...ratingInfo.upgrades.heating,
                    full_year: value,
                  },
                },
              }),
            );
          }}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Partial Year Upgrade"
          name="upgrades_heating_partial_year"
          value={ratingInfo.upgrades.heating.partial_year || ''}
          onChange={(event: any) => {
            const { value } = event.target;
            dispatch(
              changeDwellingRatingInformation({
                index,
                name: 'upgrades',
                value: {
                  ...ratingInfo.upgrades,
                  heating: {
                    ...ratingInfo.upgrades.heating,
                    partial_year: value,
                  },
                },
              }),
            );
          }}
          disabled
        />
      </SimpleGrid>
    </Box>
  );
};

export default HeatingSystem;
